import { useRef, useState } from 'react'

import { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  Box,
  Colors,
  InfiniteScroll,
  Tag,
  Text,
  ThemeColors,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagStatus, IconType } from '@factorialco/f0-react'
import { Counter } from '@factorialco/f0-react/dist/experimental'
import { assignInlineVars } from '@vanilla-extract/dynamic'

import { useEffect } from 'modules/core/hooks/useEffect'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import theme from 'modules/core/lib/theme'

import { CardShadow, KanbanCard } from 'modules/project_management/components/Kanban/Card'
import { cardBorderColor } from 'modules/project_management/components/Kanban/index.vanilla.css'
import * as stylesRefs from 'modules/project_management/components/Kanban/index.vanilla.css'
import {
  DragItem,
  StatusTagColor,
  TCardData,
  TColumn,
  getColumnData,
  isCardData,
  isCardDropTargetData,
  isColumnData,
  isColumnVariables,
  isShallowEqual,
} from 'modules/project_management/components/Kanban/utils'

const idleState = { type: 'idle' } satisfies TColumnState
type TColumnState =
  | {
      type: 'is-card-over'
      isOverChildCard: boolean
      dragging: DOMRect
    }
  | {
      type: 'idle'
    }

interface Edge {
  cursor: string
  node: unknown
}

export interface KanbanColumnData<TItem = unknown> {
  items: Array<{
    id: string
    title: string
    position?: string | null
    item?: TItem | undefined
    projections: React.ReactNode[]
    onClickCard?: () => void
  }>
  totalCount: number
}

export interface KanbanColumnType<TVariables> {
  id: string
  title: string
  statusColor: StatusTagColor | Colors | ThemeColors
  variables: TVariables
}

export interface IColumnAction {
  icon: IconType
  label: string
  onClick: (columnId: string) => void
}

interface IColumnProps<TQuery, TVariables, TItem = unknown> {
  columnAction?: IColumnAction
  column: TColumn<TVariables>
  dataSource: (data: NonNullable<TQuery>) => {
    edges: Edge[]
    pageInfo: { hasNextPage: boolean; endCursor: string }
  }
  document: TypedDocumentNode<TQuery, TVariables>
  handleDrop: (
    originalItem: DragItem<TVariables, TItem>,
    dropColumn: TVariables,
    beforePosition?: string | null,
    afterPosition?: string | null
  ) => void
  parseData: (data: TQuery | undefined) => KanbanColumnData<TItem>
}

const KanbanColumn = <TQuery, TVariables extends OperationVariables, TItem = unknown>({
  columnAction,
  column,
  dataSource,
  document,
  handleDrop,
  parseData,
}: IColumnProps<TQuery, TVariables, TItem>) => {
  const cx = useClassNames(stylesRefs)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const outerFullHeightRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [columnState, setColumnState] = useState<TColumnState>(idleState)
  const [hover, setHover] = useState(false)

  const {
    loading,
    data,
    controls: { fetchMore, hasMore },
  } = usePaginatedQuery(document, { variables: column.variables, extractConnection: dataSource })

  const { items, totalCount } = parseData(data)
  const itemsKey = items.map((item) => `${item.id}:${item.position}`).join(',')

  useEffect(() => {
    const outer = outerFullHeightRef.current
    const inner = innerRef.current
    if (!outer || !inner) return

    const data = getColumnData({ column })

    const setIsCardOver = ({
      cardData,
      location,
    }: {
      cardData: TCardData<TItem>
      location: DragLocationHistory
    }) => {
      const innerMost = location.current.dropTargets[0]
      const isOverChildCard = Boolean(innerMost && isCardDropTargetData<TItem>(innerMost.data))

      const proposed: TColumnState = {
        type: 'is-card-over',
        dragging: cardData.rect,
        isOverChildCard,
      }

      setColumnState((current) => {
        if (isShallowEqual(proposed, current)) {
          return current
        }
        return proposed
      })
    }

    return combine(
      dropTargetForElements({
        element: outer,
        getData: () => data,
        getIsSticky: () => true,
        onDropTargetChange({ source, location }) {
          if (isCardData<TItem>(source.data)) {
            setIsCardOver({ cardData: source.data, location })
            return
          }
        },
        onDragLeave({ source }) {
          if (isColumnData(source.data) && source.data.column.id === column.id) {
            return
          }
          setColumnState(idleState)
        },
        onDrop(source) {
          setColumnState(idleState)
          if (isCardData<TItem>(source.source.data)) {
            const columnData = source.location.initial.dropTargets[1]?.data
            if (!columnData || !isColumnData(columnData)) return

            // Get the closest edge of the drop target
            const closestCard = source.location.current.dropTargets[0]?.data
            const symbols = Object.getOwnPropertySymbols(closestCard)
            const closestEdgeSymbol = symbols.find(
              (sym) => sym.toString() === 'Symbol(closestEdge)'
            )
            const closestEdgeValue =
              closestCard && closestEdgeSymbol ? closestCard[closestEdgeSymbol] : undefined

            // Get the index of prev and next card
            const closestCardId =
              closestCard && isCardDropTargetData<TItem>(closestCard)
                ? closestCard.card.id
                : undefined

            const columnCards = items
            let beforePosition: string | null | undefined
            let afterPosition: string | null | undefined

            if (closestCardId) {
              // Dropping on a specific card
              const indexOfTheClosestCard = columnCards.findIndex((c) => c.id === closestCardId)
              const indexOfNextCard =
                closestEdgeValue === 'top' ? indexOfTheClosestCard - 1 : indexOfTheClosestCard + 1

              beforePosition =
                closestEdgeValue === 'top'
                  ? columnCards[indexOfNextCard]?.position
                  : columnCards[indexOfTheClosestCard]?.position
              afterPosition =
                closestEdgeValue === 'top'
                  ? columnCards[indexOfTheClosestCard]?.position
                  : columnCards[indexOfNextCard]?.position
            } else {
              // Dropping directly on column - append to end
              beforePosition = columnCards[columnCards.length - 1]?.position
              afterPosition = undefined
            }

            const originalColumnVariables = columnData.column.variables
            if (!isColumnVariables<TVariables>(originalColumnVariables)) return

            const dropColumnVariables = data.column.variables
            const originalCardId = source.source.data.card.id
            const originalCardTitle = source.source.data.card.title

            handleDrop(
              {
                id: originalCardId,
                title: originalCardTitle,
                item: source.source.data.card.item ?? undefined,
                sourceColumnVariables: originalColumnVariables,
              },
              {
                ...dropColumnVariables,
              },
              beforePosition,
              afterPosition
            )
          }
        },
      })
    )
  }, [column.id, itemsKey, loading])

  if (loading) return null

  const tagColor = column.statusColor
  const isValidStatus = (status: string): status is StatusTagColor => {
    return ['neutral', 'info', 'positive', 'warning', 'critical'].includes(status)
  }

  return (
    <Box
      ref={outerFullHeightRef}
      background={'grey100'}
      borderRadius={{ all: 'abs012' }}
      padding="s4"
      width="fit-content"
      height="fit-content"
      minHeight="s200"
      style={{
        maxHeight: '800px',
      }}
    >
      <Box flexDirection="row" gap="s4" width="s300" paddingBottom="s4" alignItems="center">
        {isValidStatus(tagColor) ? (
          <Box padding="s4">
            <F0TagStatus text={column.title} variant={tagColor} />
          </Box>
        ) : (
          <Tag leftItem="icon" icon="circle" iconColor={tagColor} type={'transparent'}>
            {column.title}
          </Tag>
        )}
        <Counter value={totalCount} size="md" />
      </Box>
      <Box ref={scrollContainerRef} overflowY="scroll" flexDirection="column" gap="s8">
        <Box ref={innerRef}>
          <InfiniteScroll
            hasMore={hasMore}
            loadMore={fetchMore}
            initialLoad={false}
            scrollContainerRef={scrollContainerRef}
          >
            {items.map((card) => (
              <KanbanCard<TItem>
                key={card.id}
                card={card}
                columnId={column.id}
                onClickCard={card.onClickCard}
              />
            ))}
            {columnState.type === 'is-card-over' && !columnState.isOverChildCard ? (
              <CardShadow dragging={columnState.dragging} />
            ) : null}
            {columnAction && (
              <Box
                flexDirection="row"
                gap="s8"
                onClick={() => columnAction.onClick(column.id)}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                className={cx('kanbanCardStyle')}
                style={assignInlineVars({
                  [cardBorderColor]: hover ? theme.colors.grey600 : theme.colors.grey300,
                })}
              >
                <F0Icon icon={columnAction.icon} className="text-f1-icon" aria-hidden />
                <Text size="200" weight="medium" color="grey800">
                  {columnAction.label}
                </Text>
              </Box>
            )}
          </InfiniteScroll>
        </Box>
      </Box>
    </Box>
  )
}

export default KanbanColumn
